import { Banner, BlockStack, Layout, Text } from '@shopify/polaris';
import { useLanguage } from '../providers/LanguageProvider';
import type { SizeCoverageReport, SizeGuideEntryView } from '../types';

export interface SizeCoverageBannerProps {
  /** Last coverage check for this shop, or null before one has ever run. */
  report: SizeCoverageReport | null;
  /** The shop's current size guides. Used only to resolve a report guide id
   * to the name a merchant recognises — never as a source of coverage data
   * itself, which always comes from `report`. */
  guides: SizeGuideEntryView[];
  onGoToSettings: () => void;
}

/**
 * Dashboard-level heads-up for a size guide that silently serves nothing.
 * Without this, a merchant only learns about a mismatched guide or an
 * uncovered product by opening Settings and reading each guide's card, or by
 * a shopper quietly never getting a size step at all.
 *
 * Reads `report.guides` and `report.uncovered` directly and mostly ignores
 * `report.error` on purpose: the coverage service preserves every guide it
 * already sampled when a later GraphQL call fails (buildCoverageReport on
 * the backend), so a set `error` does not make the per-guide numbers here
 * wrong, only incomplete for guides past the failure point. SizeGuideCard's
 * own coverage row follows the same priority for the same reason: the two
 * surfaces read the same report and must never contradict each other about
 * a guide both of them have real data for. The one thing `error` DOES
 * change here is whether the banner shows up at all when there is otherwise
 * nothing to name (see `incomplete` below) — a report that could not finish
 * is itself worth a heads-up, not just the findings a finished one turns up.
 *
 * Owns its own Layout.Section, like TrialQuotaBanner and ReviewPromptBanner,
 * so a shop with nothing to say gets no empty section.
 */
export function SizeCoverageBanner({ report, guides, onGoToSettings }: SizeCoverageBannerProps) {
  const { t } = useLanguage();
  const s = t.dashboard;

  // Nothing to check yet, and nothing left to check against: a shop that
  // removed every guide keeps its last report rather than having it erased
  // (refreshShopSizeCoverage's own comment), which would otherwise resurface
  // as a warning about a setup that no longer exists.
  if (!report || guides.length === 0) return null;

  // A guide the report sampled that matched none of the products it is
  // assigned to. `sampled > 0` excludes the fallback guide's trivial 0/0
  // (it has no categories to sample against — see isFallbackGuide on the
  // backend) and any guide the check never reached at all. A report guide id
  // with no matching entry in `guides` belongs to a guide since deleted or
  // renamed away from what this stale row remembers, so it is dropped rather
  // than named.
  const mismatchedGuides = report.guides
    .filter((g) => g.sampled > 0 && g.matched === 0)
    .map((g) => ({ id: g.id, name: guides.find((entry) => entry.id === g.id)?.name }))
    .filter((g): g is { id: string; name: string } => Boolean(g.name));

  const uncoveredCount = report.uncovered.count;
  // A shop-wide sample/request cap, or a GraphQL failure, stopped the last
  // check before it finished (buildCoverageReport). Some guides may carry no
  // real data at all yet and would otherwise say nothing here — a merchant
  // must not read that silence as "checked, all clear".
  const incomplete = Boolean(report.error);

  if (mismatchedGuides.length === 0 && uncoveredCount === 0 && !incomplete) return null;

  return (
    <Layout.Section>
      <Banner
        tone="warning"
        title={s.sizeCoverageTitle}
        action={{ content: s.sizeCoverageAction, onAction: onGoToSettings }}
      >
        <BlockStack gap="100">
          {mismatchedGuides.map((g) => (
            <Text as="p" variant="bodyMd" key={g.id}>
              {s.sizeCoverageGuideMismatch(g.name)}
            </Text>
          ))}
          {uncoveredCount > 0 && (
            <Text as="p" variant="bodyMd">
              {s.sizeCoverageUncovered(uncoveredCount)}
            </Text>
          )}
          {incomplete && (
            <Text as="p" variant="bodyMd">
              {s.sizeCoverageCheckIncomplete}
            </Text>
          )}
        </BlockStack>
      </Banner>
    </Layout.Section>
  );
}

export default SizeCoverageBanner;
